import React from "react";
import { Box, Dialog } from "@mui/material";
import { dt } from "../../../../shared/themes/redesignTokens";
import useTranslation from "../../../../shared/hooks/useTranslation";
import {
  ReadinessVM,
  ReadinessModel,
  midasDotLit,
  hasDocs as hasDocsFn,
  liveDotVisual,
  liveDotState,
  popupDocRows,
  fanoutProvenanceLabel,
} from "../../utils/midasReadiness";
import {
  rp,
  ReadinessHeaderPill,
  SectionBadge,
  OemSupportPanel,
  DocTypeRow,
  OutlineCta,
  OemPackageMutedLine,
  DOC_TYPE_KEYS,
} from "./readinessPopupParts";

// =====================================================================
//  MidasReadinessPopup (DR-3) — ONE shared component that self-configures
//  into three states from its props (Paper artboards v2-2b / 2c / 2d):
//    - OEM state              : dark-gold OEM panel on top + both CTAs
//    - non-OEM CONNECTED state : no panel, live=CONNECTED, no CTA
//    - EMPTY / with-docs state : honest empty (or doc list) + Request CTA
//
//  ALL derivation flows through `midasReadiness.ts` (single source of truth);
//  the header LIVE dot renders from `liveDotVisual` so it matches every other
//  surface. NO "Ask MIDAS" / chat affordance anywhere. Languages are shown as
//  metadata only, with the disclaimer in every state.
//
//  `liveStatus` is only meaningful on machine-instance surfaces (the profile
//  card passes it, fail-closed). On the catalog it is omitted → capability-only.
// =====================================================================

const SALES_MAILTO = "sales@machine-id.com";

export interface MidasReadinessPopupProps {
  open: boolean;
  onClose: () => void;
  readiness?: ReadinessVM | null;
  isOemPartner?: boolean;
  oemPartnerName?: string | null;
  /** Real gateway connection state — instance surfaces only (fail-closed). */
  liveStatus?: "connected" | "disconnected";
  /** Model or machine display name shown as the popup title. */
  machineOrModelName: string;
  /** Manufacturer display name — used only for the non-OEM "not offered" line. */
  manufacturerName?: string | null;
  /**
   * OEM contact fields (Q-DR-2). Not on the VM today → callers omit them and
   * the sales mailto fallback ships. Structured so they can arrive later.
   */
  oemContactUrl?: string | null;
  oemContactEmail?: string | null;
}

/** Build a mailto: URL with every dynamic value encoded (SEC-REQ-8). */
const buildMailto = (email: string, subject: string, body?: string): string => {
  const params = [`subject=${encodeURIComponent(subject)}`];
  if (body) params.push(`body=${encodeURIComponent(body)}`);
  return `mailto:${email}?${params.join("&")}`;
};

/**
 * Accept an OEM contact address only when it is a single, well-formed mailto
 * target (SEC-REQ-8, item 11): must contain "@" and must NOT contain a query
 * separator (`?` / `&`) or whitespace that could inject extra mailto headers
 * or a second recipient. Anything else → reject (caller falls back to sales@).
 */
const isSafeMailtoAddress = (email?: string | null): email is string =>
  !!email && email.includes("@") && !/[?&\s]/.test(email);

const MidasReadinessPopup: React.FC<MidasReadinessPopupProps> = ({
  open,
  onClose,
  readiness,
  isOemPartner,
  oemPartnerName,
  liveStatus,
  machineOrModelName,
  manufacturerName,
  oemContactUrl,
  oemContactEmail,
}) => {
  const { translate } = useTranslation("myMachinery");

  const model: ReadinessModel = { readiness, isOemPartner, oemPartnerName };
  const isOem = isOemPartner === true;
  const connected = liveStatus === "connected";
  const hasDocs = hasDocsFn(readiness);

  const midasLit = midasDotLit(readiness);
  const liveVisual = liveDotVisual(readiness, { connected });
  const liveState = liveDotState(readiness, { connected });

  // Formatted, uppercase languages for lit doc rows (model-level metadata).
  const langLabel =
    readiness?.languages && readiness.languages.length > 0
      ? readiness.languages.map((l) => l.toUpperCase()).join(" · ")
      : "—";

  const provenanceText = (
    kind: string,
    fanoutSource?: string
  ): string => {
    switch (kind) {
      case "direct-oem":
        return translate("provenanceDirectOem", { name: oemPartnerName || translate("theManufacturer") });
      case "direct-owner":
        return translate("provenanceDirectOwner");
      case "fanout":
        return translate("provenanceFanout", {
          source: fanoutProvenanceLabel(fanoutSource || ""),
        });
      default:
        return translate("notIngestedYet");
    }
  };

  const docRows = popupDocRows(model);

  // --- CTA handlers ---
  const onRequestIntegration = () => {
    const subject = translate("mailRequestSubject", { name: machineOrModelName });
    const body = translate("mailRequestBody", { name: machineOrModelName });
    window.location.href = buildMailto(SALES_MAILTO, subject, body);
  };

  const onContactOem = () => {
    // Render/open a ContactUrl only if it is an http(s) URL (SEC-REQ-8).
    if (oemContactUrl && /^https?:\/\//i.test(oemContactUrl)) {
      window.open(oemContactUrl, "_blank", "noopener,noreferrer");
      return;
    }
    const oem = oemPartnerName || translate("theManufacturer");
    const subject = translate("mailContactOemSubject", { oem, name: machineOrModelName });
    const email = isSafeMailtoAddress(oemContactEmail) ? oemContactEmail : SALES_MAILTO;
    window.location.href = buildMailto(email, subject);
  };

  // --- Live block copy + badge ---
  const liveBadge: { label: string; tone: "ready" | "muted" } = connected
    ? { label: translate("liveConnected").toUpperCase(), tone: "ready" }
    : liveState === "proven" || liveState === "possible"
      ? { label: translate("livePossible").toUpperCase(), tone: "muted" }
      : { label: translate("liveNotConnected").toUpperCase(), tone: "muted" };

  const liveBody = connected
    ? translate("liveConnectedBody")
    : isOem
      ? translate("liveOemPossibleBody", { oem: oemPartnerName || translate("theManufacturer") })
      : translate("liveEdgeApplianceBody");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      // Item 1: the popup is rendered as a JSX child of its opener (catalog card,
      // machinery card, profile card). MUI portals its DOM to <body>, but React
      // events still bubble along the COMPONENT tree — so a click on Close / the
      // backdrop / a CTA would reach the opener card's onClick and navigate.
      // Stop propagation at the Dialog root (covers every current + future
      // opener); onClose still fires from MUI's own backdrop handler underneath.
      onClick={(e) => e.stopPropagation()}
      maxWidth={false}
      slotProps={{ backdrop: { sx: { backgroundColor: "rgba(12, 17, 23, 0.35)" } } }}
      PaperProps={{
        sx: {
          width: "640px",
          maxWidth: "calc(100vw - 32px)",
          // Bounded flex column: cap at the viewport (minus our 16px margins) so
          // the paper never grows taller than the screen on short viewports /
          // long localized content. `overflow: hidden` stays for the rounded-
          // corner clipping — the scrollable region is the inner content Box.
          maxHeight: "calc(100vh - 32px)",
          display: "flex",
          flexDirection: "column",
          m: 2,
          borderRadius: "12px",
          boxShadow: "0 24px 64px rgba(12,17,23,0.35)",
          overflow: "hidden",
          fontFamily: dt.font,
        },
      }}
    >
      {/* Inner content scrolls within the corner-clipped paper so every section
          (live/OEM/footer + Close) stays reachable when height is constrained. */}
      <Box
        sx={{
          backgroundColor: "#FFFFFF",
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "12px",
            padding: "20px 24px",
            borderBottom: `1px solid ${rp.headerBorder}`,
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: "3px", minWidth: 0 }}>
            <Box sx={{ fontSize: "11px", fontWeight: 600, fontFamily: dt.font, letterSpacing: "0.08em", lineHeight: "14px", color: rp.eyebrow }}>
              {translate("midasDataReadiness")}
            </Box>
            <Box
              sx={{
                fontSize: "18px",
                fontWeight: 600,
                fontFamily: dt.font,
                lineHeight: "24px",
                color: rp.title,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {machineOrModelName}
            </Box>
          </Box>
          <ReadinessHeaderPill midasLit={midasLit} liveVisual={liveVisual} oemLit={isOem} />
        </Box>

        {/* OEM dark-gold panel (OEM state only) */}
        {isOem && (
          <OemSupportPanel
            eyebrow={translate("fullOemSupport")}
            title={translate("oemPanelTitle", { oem: oemPartnerName || translate("theManufacturer") })}
            body={translate("oemPanelBody")}
            ctaLabel={translate("contactOem", { oem: oemPartnerName || translate("theManufacturer") })}
            onCta={onContactOem}
          />
        )}

        {/* Machine knowledge */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: "12px", m: "20px 24px 0 24px" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Box sx={{ fontSize: "14px", fontWeight: 600, fontFamily: dt.font, lineHeight: "18px", color: rp.sectionHeading }}>
              {translate("machineKnowledge")}
            </Box>
            <SectionBadge
              label={(hasDocs ? translate("knowledgeReady") : translate("knowledgeNoDocs")).toUpperCase()}
              tone={hasDocs ? "ready" : "muted"}
            />
          </Box>

          {docRows.map((row, i) => (
            <DocTypeRow
              key={row.docType}
              chipLabel={translate(DOC_TYPE_KEYS[row.docType])}
              lit={row.lit}
              languages={row.lit ? langLabel : "—"}
              provenance={provenanceText(row.provenance, row.fanoutSource)}
              isFirst={i === 0}
            />
          ))}

          {/* Item 6: empty-state lead-gen line (approved artboard v2-2d) — only
              when no documents are ingested. Frames doc-upload as MIDAS value. */}
          {!hasDocs && (
            <Box sx={{ fontSize: "12px", fontFamily: dt.font, lineHeight: "17px", color: rp.liveBody }}>
              {translate("emptyStateNote")}
            </Box>
          )}

          <Box sx={{ fontSize: "11px", fontStyle: "italic", fontFamily: dt.font, lineHeight: "15px", color: rp.disclaimer }}>
            {translate("languagesDisclaimer")}
          </Box>
        </Box>

        {/* Live machine data */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: "14px",
            m: "16px 24px 0 24px",
            pt: "16px",
            borderTop: `1px solid ${rp.rowBorder}`,
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1, gap: "6px", minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Box sx={{ fontSize: "14px", fontWeight: 600, fontFamily: dt.font, lineHeight: "18px", color: rp.sectionHeading }}>
                {translate("liveMachineData")}
              </Box>
              <SectionBadge label={liveBadge.label} tone={liveBadge.tone} />
            </Box>
            <Box sx={{ fontSize: "12px", fontFamily: dt.font, lineHeight: "17px", color: rp.liveBody }}>
              {liveBody}
            </Box>
            {connected && (
              <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <Box sx={{ width: "6px", height: "6px", borderRadius: "50%", flexShrink: 0, backgroundColor: rp.liveGreenDot }} />
                <Box sx={{ fontSize: "11px", fontFamily: dt.font, lineHeight: "14px", color: rp.provText }}>
                  {translate("connectedViaEdge")}
                </Box>
              </Box>
            )}
          </Box>
          {!connected && (
            <OutlineCta label={translate("requestIntegration")} onClick={onRequestIntegration} />
          )}
        </Box>

        {/* Muted OEM package line (non-OEM states) */}
        {!isOem && (
          <OemPackageMutedLine
            heading={translate("officialOemPackage")}
            body={translate("oemNotOffered", { manufacturer: manufacturerName || translate("theManufacturer") })}
          />
        )}

        {/* Footer */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            backgroundColor: rp.footerBg,
            borderTop: `1px solid ${rp.footerBorder}`,
            mt: "20px",
            padding: "14px 24px",
          }}
        >
          {/* Item 13: real <button> — keyboard-focusable, Enter/Space activate. */}
          <Box
            component="button"
            type="button"
            onClick={onClose}
            sx={{
              border: "none",
              background: "none",
              p: 0,
              m: 0,
              fontSize: "13px",
              fontWeight: 600,
              fontFamily: dt.font,
              lineHeight: "16px",
              color: rp.footerText,
              cursor: "pointer",
              borderRadius: "4px",
              "&:hover": { color: dt.textPrimary },
              "&:focus-visible": { outline: `2px solid ${rp.midasText}`, outlineOffset: "2px" },
            }}
          >
            {translate("close")}
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default MidasReadinessPopup;
